import { useMemo } from 'react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { format } from 'date-fns';
import { useApp } from '~/hooks/useApp';
import { banks } from '~/hooks/constants';
import { toThousandCommas } from '~/filters';
import { Tooltip } from '~/components/commons/Tooltip';
import { DataTable } from '~/components/commons/DataTable/DataTable';
import ModalAdminBalanceRefund from '~/components/modals/sms/ModalAdminBalanceRefund';
import ModalEditClinicMemo from '~/components/modals/clinic/ModalEditClinicMemo';
import ModalSmsTotalInfo from '../../modals/sms/ModalSmsTotalInfo';

type SmsTableProps = {
  items: any[];
  callApi: () => void;
};

export const SmsDataTable = observer((props: SmsTableProps) => {
  const { items, callApi } = props;
  const app = useApp();
  const onAddMemo = (memo: any, id: number) => {
    openModalEditClinicMemo(memo, -1, id);
  };

  const onClickMemo = (memo: any, idx: number, id: number) => {
    openModalEditClinicMemo(memo, idx, id);
  };

  const openModalEditClinicMemo = (
    memo: any,
    memoIndex: number,
    id: number
  ) => {
    let clinic: any;

    if (memo == null) {
      clinic = { memo: [], id: id };
    } else {
      clinic = { memo: memo, id: id };
    }

    runInAction(() => {
      app.$modal.custom({
        component: ModalEditClinicMemo,
        options: {
          clinic,
          memoIndex,
          callApi,
        },
      });
    });
  };

  const onRefundModal = (item: any) => {
    runInAction(() => {
      app.$modal.custom({
        component: ModalAdminBalanceRefund,
        options: {
          clinicId: item.clinicId,
          data: item,
          callApi: callApi,
        },
      });
    });
  };

  const requestTypeKey: {
    [index: 'deposit' | 'refund' | string]: '충전' | '환불' | undefined;
  } = {
    deposit: '충전',
    refund: '환불',
  };

  const typeKey: {
    [index: 'paid' | 'free' | string]: '유료' | '무료충전' | undefined;
  } = {
    paid: '유료', //유상
    free: '무료충전', //무상
  };

  const statusKey: {
    [index: 'request' | 'complete' | 'canceled' | 'expired' | string]:
      | '신청'
      | '완료'
      | '취소'
      | '충전취소 (입금기한만료)'
      | undefined;
  } = {
    request: '신청',
    complete: '완료', //처리완료
    canceled: '취소',
    expired: '충전취소 (입금기한만료)', //입금기한만료
  };

  const refundDiv = (str: string, item: any) => {
    return (
      <div className="sms-status">
        <div className="text-center">
          <div>{str}</div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <button
              onClick={() => onRefundModal(item)}
              className="btn btn-primary btn-sm btn-refund"
            >
              환불
            </button>
          </div>
        </div>
      </div>
    );
  };

  const statusCombination = (item: any) => {
    if (item.type === 'free') {
      if (item.requestType === 'deposit') {
        return '무료충전';
      } else {
        return '무료환불';
      }
    } else {
      if (item.status === 'expired') {
        return statusKey[item.status];
      } else {
        const str = `${requestTypeKey[item.requestType]}${
          statusKey[item.status]
        }`;
        if (item.status === 'request' && item.requestType === 'refund') {
          return refundDiv(str, item);
        } else {
          return `${str}`;
        }
      }
    }

    //     if (item.type === 'free') {
    //       return typeKey[item.type];
    //     } else {
    //       if (item.status === 'expired') {
    //         return statusKey[item.status];
    //       } else {
    //         const str = `${requestTypeKey[item.requestType]}${
    //           statusKey[item.status]
    //         }`;
    //         if (item.status === 'request' && item.requestType === 'refund') {
    //           return refundDiv(str, item);
    //         } else {
    //           return `${str}`;
    //         }
    //       }
    //     }
  };

  const onSmsTotalInfo = (data: any) => {
    runInAction(() => {
      app.$modal.custom({
        component: ModalSmsTotalInfo,
        options: {
          totalPaidAmount: data.totalPaidAmount ? data.totalPaidAmount : 0,
          totalFreeAmount: data.totalFreeAmount ? data.totalFreeAmount : 0,
          totalRefundAmount: data.totalRefundAmount
            ? data.totalRefundAmount
            : 0,
          totalPaidCount: data.totalPaidCount ? data.totalPaidCount : 0,
          totalFreeCount: data.totalFreeCount ? data.totalFreeCount : 0,
          paidBalance: data.paidBalance ? data.paidBalance : 0,
          freeBalance: data.freeBalance ? data.freeBalance : 0,
          confirm,
          callApi,
        },
      });
    });
  };

  const getFirstValue = (item: any, keys: string[]) =>
    keys.map((key) => item[key]).find((value) => value) || '';

  // 상태, 신청일, 상호(병원명), 사업자번호, 신청금액, 병원 입금내역 - 입금일, 금액(VAT포함), 입금자명, 가상계좌번호, 최근 상태 변경일, 사유, 작업자, CS 메모
  const schema = useMemo(
    () => ({
      headers: [
        {
          id: 'clinicDepositHistory',
          name: '병원 입금내역',
        },
      ],
      columns: [
        {
          id: 'status',
          name: '상태',
          headerStyle: { minWidth: '120px' },
          value: (item: any) => item.displayStatus || '-',
        },
        {
          id: 'requestAt',
          name: '신청일',
          headerStyle: { minWidth: '140px' },
          value: (item: any) =>
            item.requestAt
              ? format(new Date(item.requestAt), 'yyyy-MM-dd HH:mm:ss')
              : '-',
        },
        {
          id: 'clinicName',
          name: '상호(병원명)',
          headerStyle: { minWidth: '186px' },
          value: (item: any) => {
            return (
              <div
                className="td-underline cursor-pointer"
                onClick={() => onSmsTotalInfo(item)}
              >
                {item.clinicName}
              </div>
            );
          },
        },
        {
          id: 'employerNo',
          name: '사업자번호',
          headerStyle: { minWidth: '186px' },
          value: (item: any) => item.employerNo || '-',
        },
        {
          id: 'amount',
          name: '신청금액',
          headerStyle: { minWidth: '120px' },
          value: (item: any) =>
            item.amount ? toThousandCommas(item.amount) : '-',
        },
        {
          id: 'depositAmount',
          name: '입금액(VAT포함)',
          headerStyle: { minWidth: '120px' },
          value: (item: any) =>
            item.depositAmount ? toThousandCommas(item.depositAmount) : '-',
        },

        {
          id: 'completeAt',
          name: '입금일',
          parentHeader: 'clinicDepositHistory',
          headerStyle: { minWidth: '140px' },
          value: (item: any) =>
            item.completeAt
              ? format(new Date(item.completeAt), 'yyyy-MM-dd')
              : '-',
        },
        {
          id: 'createdDate',
          name: '금액(VAT포함)',
          parentHeader: 'clinicDepositHistory',
          headerStyle: { minWidth: '140px' },
          value: (item: any) =>
            item.paidAmount ? toThousandCommas(item.paidAmount) : '-',
        },
        {
          id: 'depositorName',
          name: '입금자명',
          parentHeader: 'clinicDepositHistory',
          headerStyle: { minWidth: '140px' },
          value: (item: any) => item.acctNm || '-',
        },
        {
          id: 'bankCd',
          name: '가상계좌번호',
          headerStyle: { minWidth: '240px' },
          value: (item: any) => item.acctNo || '-',
        },
        {
          id: 'updatedAt',
          name: '최근 상태 변경일',
          headerStyle: { minWidth: '140px' },
          value: (item: any) =>
            item.updatedAt
              ? format(new Date(item.updatedAt), 'yyyy-MM-dd HH:mm:ss')
              : '-',
        },
        {
          id: 'reason',
          name: '사유',
          headerStyle: { maxWidth: '280px' },
          value: (item: any) => item.reason || '-',
        },
        {
          id: 'creatorName',
          name: '작업자',
          headerStyle: { minWidth: '140px' },
          value: (item: any) => item.creatorName || '-',
        },
        {
          id: 'memo',
          name: 'CS 메모',
          headerStyle: { minWidth: '280px' },
          value: (item: any) => (
            <>
              <div style={{ width: '100%', textAlign: 'left' }}>
                {item.memo !== '' && item.memo !== null && item.memo !== '[]'
                  ? JSON.parse(item.memo).map((memoItem: any, idx: number) => {
                      return (
                        <div
                          onClick={() =>
                            onClickMemo(item.memo, idx, item.clinicId)
                          }
                          className="td-underline"
                          key={idx}
                          style={{ cursor: 'pointer' }}
                        >
                          {memoItem.content} ({memoItem.timestamp})
                        </div>
                      );
                    })
                  : null}
              </div>
              <Tooltip
                title={app.$me.role === 'manager' ? '권한이 없습니다.' : ''}
              >
                <div
                  style={{
                    float: 'left',
                    marginTop: '4px',
                  }}
                >
                  <button
                    onClick={() => onAddMemo(item.memo, item.clinicId)}
                    className="btn-sm btn-info"
                    disabled={app.$me.role === 'manager'}
                  >
                    추가
                  </button>
                </div>
              </Tooltip>
            </>
          ),
        },
      ],
    }),
    []
  );

  return (
    <DataTable
      data={items}
      schema={schema}
      resizable
      placeholder={'조회 결과가 없습니다.'}
    />
  );
});
